import { Data2, useDataTypesHooks } from "../../../features/data-types"
import { fileHelpers, useFileActions } from "../../../features/files"
import { O } from "../../../features/ui"
import { TableName } from "../../../tables"
import { round } from "../../../utils/typeUtils"
import { useAdjustmentNoteService, AdjustmentNoteType } from "../../administration"
import { FiscalDocumentStatus } from "../../fiscal-documents"
import { useBranchLoaders } from "../../manager"
import { useRoadmapActions, useRoadmapLoaders } from "../../logistics"
import { Item, ItemDeliveryStats, useOperationBuilders, useOperationLoaders, useOperationService } from "../../operations"
import { useLotActions, useLotBuilders, useLotLoaders } from "../../products"
import { HoardOrder, SaleOperation, useSaleOperationLoaders, useSaleOperationService } from "../../sales"
import { hoardHelpers } from "../../sales/hooks/hoardHooks"
import { useStockService } from "../../stock"
import { useDeliveryActions } from "../actions/deliveryActions"
import { useDeliveryBuilders } from "../builders/deliveryBuilders"
import { DeliveryItemLotData, INVOICE_IN_FLIGHT_MESSAGE, NoteGenerationType } from "../components/types"
import { deliveryOrderHelpers, useDeliveryOrderHooks } from "../hooks/deliveryOrderHooks"
import { deliveryHelpers } from "../hooks/deliveryHooks"
import { useDeliveryLoaders } from "../loaders/deliveryLoaders"
import { useDeliveryOrderLoaders } from "../loaders/deliveryOrderLoaders"
import { Delivery, DeliveryItem, DeliveryItemDeliveryStats, DeliveryMethod, DeliveryOrder, DeliveryStatus, NotDeliveredItem } from "../state/types"
import { useDeliveryOrderService } from "./deliveryOrderService"
import { DeliveryStatsDiff } from "./types"

export const useDeliveryService = () => {
    const { buildDelivery, buildDeliveryItem } = useDeliveryBuilders()
    const { buildItem } = useOperationBuilders()
    const { buildLot } = useLotBuilders()

    const { hasNothingToDeliver } = deliveryOrderHelpers
    const { describeDeliveryCode } = deliveryHelpers
    const { deliveryNoteSubPath2 } = fileHelpers
    const { generateCode } = useDataTypesHooks()
    const { addNotDeliveredCount, removeNotDeliveredCount } = useDeliveryOrderHooks()
    
    const branchLoaders = useBranchLoaders()
    const deliveryloaders = useDeliveryLoaders()
    const deliveryOrderLoaders = useDeliveryOrderLoaders()
    const operationLoaders = useOperationLoaders()
    const saleOperationLoaders = useSaleOperationLoaders()
    const lotLoaders = useLotLoaders()
    const roadmapLoaders = useRoadmapLoaders()

    const { updateDeliveryStats, updateDeliveryOrderProps, refreshOperationDeliveryStatus } = useDeliveryOrderService()
    const { increaseStock, reserveStock, enableStock, decreaseReservedStock } = useStockService()
    const { updateOperationProps, updateDeliveryStats: updateOperationDeliveryStats } = useOperationService()
    const { createAdjustmentNote } = useAdjustmentNoteService()
    const { applyAdjustmentNote } = useSaleOperationService()
    const { saveDelivery } = useDeliveryActions()
    const { removeDeliveryRoadmaps } = useRoadmapActions()
    const { saveFile } = useFileActions()
    const { saveLot } = useLotActions()

    const updateDeliveryProps = async (
        deliveryId: string,
        props: Partial<Delivery>,
    ) => {
        const { loadDelivery } = deliveryloaders()
        const loadedDelivery = await loadDelivery(deliveryId, false)
        const updatedDelivery = buildDelivery({
            ...loadedDelivery,
            ...props
        })
        return saveDelivery(updatedDelivery)
    }

    // Con una factura en vuelo cambian las cantidades facturadas: devoluciones, anulaciones y
    // cancelaciones esperan a que el comprobante resuelva.
    const assertNoInvoiceInFlight = (table: TableName, operation: SaleOperation) => {
        if (table === TableName.HOARD_ORDERS && (operation as HoardOrder).invoiceInFlight) {
            throw new Error(INVOICE_IN_FLIGHT_MESSAGE)
        }
    }

    // Genera las notas de una devolución o cancelación partiendo lo facturado de lo que no lo está.
    // Lo facturado lleva el tipo de nota elegido (una NC ARCA genera comprobante fiscal); lo no
    // facturado siempre es una nota interna, sin comprobante. El importe se ajusta una sola vez.
    const createNotes = async (
        table: TableName,
        operationId: string,
        operation: SaleOperation,
        itemsData: Data2[],
        noteType: NoteGenerationType,
        options: {
            applyAdjustment?: boolean
            createUninvoicedNote?: boolean
            splitMode?: 'return' | 'cancel'
            deliveredByItem?: Map<string, number>
        } = {}
    ) => {
        const {
            applyAdjustment = true,
            createUninvoicedNote = true,
            splitMode = 'cancel',
            deliveredByItem
        } = options
        const { invoiced, uninvoiced } = hoardHelpers.splitQuantitiesByInvoicing(
            operation,
            itemsData,
            splitMode,
            deliveredByItem
        )

        let amount = 0
        if (invoiced.length > 0) {
            const note = await createAdjustmentNote(
                { type: AdjustmentNoteType.CREDIT, table, operationId },
                invoiced,
                operation,
                noteType
            )
            amount = round(amount + note.amount)
        }
        if (createUninvoicedNote && uninvoiced.length > 0) {
            const note = await createAdjustmentNote(
                {
                    type: AdjustmentNoteType.CREDIT,
                    table,
                    operationId,
                    fiscalStatus: FiscalDocumentStatus.NOT_APPLICABLE
                },
                uninvoiced,
                operation,
                NoteGenerationType.ADJUSTMENT_NOTE
            )
            amount = round(amount + note.amount)
        }

        if (applyAdjustment && amount > 0) {
            await applyAdjustmentNote(table, operationId, amount)
        }

        return amount
    }

    const createDelivery = async (
        deliveryOrderId: string,
        deliveryDate: Date,
        itemsData: Data2[],
        deliveryNoteFile?: File,
        supplierDeliveryNoteNumber?: string
    ) => {
        // A delivery without quantities wouldn't move the order stats, leaving an empty delivery note
        if (!itemsData.some(itemData => (itemData.quantity as number) > 0)) {
            throw new Error('El remito debe tener al menos un producto con cantidad mayor a 0.')
        }

        const { loadDeliveryOrder } = deliveryOrderLoaders()
        // Read from the db: a stale order would let the same pending quantities be emitted twice
        const deliveryOrder = (await loadDeliveryOrder(deliveryOrderId, false))!
        const { table, operationId, method } = deliveryOrder

        const exceedsPending = itemsData.some(itemData => {
            const orderItem = deliveryOrder.items.find(item => item.itemId === itemData.itemId)
            return (itemData.quantity as number) > (orderItem?.deliveryStats.pending || 0)
        })
        if (deliveryOrder.cancelled || exceedsPending) {
            throw new Error('Las cantidades pendientes de la orden cambiaron. Recargá la página y volvé a intentar.')
        }
        const { loadOrderDeliveries } = deliveryloaders()
        const orderDeliveries = await loadOrderDeliveries(deliveryOrderId)

        const code = await generateCode(undefined, orderDeliveries)
        const items = itemsData.map(buildDeliveryItem)
        const isShipment = method === DeliveryMethod.SHIPMENT

        let fromNotDeliveredItems: O<NotDeliveredItem[]>
        if (isShipment) {
            fromNotDeliveredItems = (deliveryOrder.notDeliveredCount || [])
                .filter(notDeliveredItem => items.map(item => item.itemId).includes(notDeliveredItem.itemId))
                .map(notDeliveredItem => {
                    const item = items.find(item => item.itemId === notDeliveredItem.itemId)!
                    return {
                        ...notDeliveredItem,
                        quantity: Math.min(notDeliveredItem.quantity, item.quantity)
                    }
                })
        }

        let delivery = buildDelivery({
            code,
            codeStr: describeDeliveryCode({ code } as Delivery, deliveryOrder),
            table,
            operationId,
            orderId: deliveryOrder.id,
            method: deliveryOrder.method,
            items,
            date: deliveryDate,
            customerId: deliveryOrder.customerId,
            branchId: deliveryOrder.branchId,
            addressId: deliveryOrder.addressId,
            deliveryStatus: isShipment ? DeliveryStatus.PENDING : DeliveryStatus.DELIVERED,
            supplierDeliveryNoteNumber,
            fromNotDeliveredItems
        })

        if (deliveryNoteFile) {
            const ext = fileHelpers.getFileExtension(deliveryNoteFile)
            const fileSubPath = deliveryNoteSubPath2(delivery, deliveryOrder, ext)
            const deliveryNoteUrl = await saveFile(fileSubPath, deliveryNoteFile)
            delivery = buildDelivery({
                ...delivery,
                deliveryNoteUrl
            })
        }

        const savedDelivery = await saveDelivery(delivery)

        const isReception = method === DeliveryMethod.RECEPTION
        const isPickup = method === DeliveryMethod.PICKUP
        const diffMap = new Map<string, Partial<DeliveryItemDeliveryStats>>()
        items.forEach(item => {
            diffMap.set(item.itemId, {
                pending: -item.quantity,
                dispatched: isReception || isPickup ? 0 : item.quantity,
                delivered: isReception || isPickup ? item.quantity : 0
            })
        })
        let newProps: Partial<DeliveryOrder> = {}
        if (fromNotDeliveredItems) {
            const newCount = removeNotDeliveredCount(deliveryOrder, fromNotDeliveredItems)
            newProps = { notDeliveredCount: newCount }
        }
        await updateDeliveryStats(deliveryOrderId, diffMap, newProps)

        const { loadOperation } = operationLoaders()
        const operation = await loadOperation(table, operationId)

        // Stock updates run sequentially: modifyStock reads and writes the same record, so parallel calls could overwrite each other
        if (isReception) {
            const { loadLots, loadProductBaseLot } = lotLoaders()
            const lotIds = itemsData
                .filter(itemData => !!itemData.lotData && (itemData.lotData as DeliveryItemLotData).lotId)
                .map(itemData => (itemData.lotData as DeliveryItemLotData).lotId!)
            const loadedLots = await loadLots(lotIds)

            const receivedItemsData = itemsData.filter(itemData => (itemData.deliveredQuantity! as number) > 0)
            for (const itemData of receivedItemsData) {
                const lotData = itemData.lotData! as DeliveryItemLotData
                const deliveredQuantity = itemData.deliveredQuantity! as number
                const operationItem = operation!.items.find(operationItem => operationItem.id === itemData.itemId)!
                const productId = operationItem.productId

                const loadedLot = loadedLots.find(loadedLot => loadedLot.id === lotData?.lotId)
                if (!!lotData && !loadedLot) {
                    const lot = buildLot({
                        code: lotData.lotCode,
                        productId,
                        attributes: lotData.lotAttributes
                    })
                    const savedLot = await saveLot(lot)
                    await increaseStock(savedLot.productId, deliveryOrder.branchId, deliveredQuantity, savedLot.id)
                } else {
                    const lot = loadedLot || await loadProductBaseLot(productId)
                    await increaseStock(productId, deliveryOrder.branchId, deliveredQuantity, lot!.id)
                }
            }
        } else if (isPickup) {
            for (const item of items) {
                const operationItem = operation!.items.find(operationItem => operationItem.id === item.itemId)!
                await decreaseReservedStock(operationItem.productId, deliveryOrder.branchId, item.deliveredQuantity, operationItem.lotId)
            }
        }

        await refreshOperationDeliveryStatus(table, operationId)

        return savedDelivery
    }

    const annulDelivery = async (deliveryId: string) => {
        const { loadDelivery } = deliveryloaders()
        const loadedDelivery = (await loadDelivery(deliveryId, false))!

        // Only what is still dispatched goes back to pending: annulling twice or annulling a completed
        // delivery note would otherwise add its quantities to the order pending again
        const dispatchedItems = loadedDelivery.items
            .map(item => ({ itemId: item.itemId, dispatched: item.quantity - item.deliveredQuantity - item.notDeliveredQuantity }))
            .filter(item => item.dispatched > 0)
        if (loadedDelivery.cancelled || dispatchedItems.length === 0) {
            throw new Error('El remito ya fue anulado o completado. Recargá la página para ver su estado actual.')
        }

        const { loadDeliveryOrder } = deliveryOrderLoaders()
        const deliveryOrder = (await loadDeliveryOrder(loadedDelivery.orderId!, false))!

        const diffMap = new Map<string, Partial<DeliveryItemDeliveryStats>>()
        dispatchedItems.forEach(item => {
            diffMap.set(item.itemId, {
                pending: item.dispatched,
                dispatched: -item.dispatched
            })
        })
        const notDeliveredCount = addNotDeliveredCount(deliveryOrder, loadedDelivery.fromNotDeliveredItems || [])
        await updateDeliveryStats(deliveryOrder.id, diffMap, { notDeliveredCount })

        const annulledDelivery = await updateDeliveryProps(deliveryId, { cancelled: true })
        await refreshOperationDeliveryStatus(loadedDelivery.table, loadedDelivery.operationId)

        // An annulled delivery note leaves its roadmap (and so its truck preparation and completion)
        const { loadDeliveriesRoadmapsLinks } = roadmapLoaders()
        const deliveryRoadmaps = await loadDeliveriesRoadmapsLinks([deliveryId], true)
        if (deliveryRoadmaps.length > 0) {
            await removeDeliveryRoadmaps(deliveryRoadmaps.map(deliveryRoadmap => deliveryRoadmap.id))
        }

        return annulledDelivery
    }

    const completeShipment = async (
        deliveryId: string,
        itemsData: Data2[],
        deliveryNoteFile?: File
    ) => {
        const { loadDelivery } = deliveryloaders()
        const delivery = (await loadDelivery(deliveryId, false))!
        const { loadDeliveryOrder } = deliveryOrderLoaders()
        const deliveryOrder = (await loadDeliveryOrder(delivery.orderId!, false))!

        const newItems: DeliveryItem[] = delivery.items.map(item => {
            const { deliveredQuantity, notDeliveredQuantity } = itemsData.find(data => data.itemId === item.itemId)!
            return {
                ...item,
                deliveredQuantity: deliveredQuantity! as number,
                notDeliveredQuantity: notDeliveredQuantity! as number
            }
        })

        let deliveryNoteUrl: O<string>
        if (deliveryNoteFile) {
            const ext = fileHelpers.getFileExtension(deliveryNoteFile)
            const fileSubPath = deliveryNoteSubPath2(delivery, deliveryOrder, ext)
            deliveryNoteUrl = await saveFile(fileSubPath, deliveryNoteFile)
        }

        const savedDelivery = await updateDeliveryProps(deliveryId, {
            items: newItems,
            deliveryStatus: DeliveryStatus.DELIVERED,
            deliveryNoteUrl
        })

        const diffMap = new Map<string, Partial<DeliveryItemDeliveryStats>>()
        newItems.forEach(item => {
            diffMap.set(item.itemId, {
                pending: item.notDeliveredQuantity,
                dispatched: -(item.deliveredQuantity + item.notDeliveredQuantity),
                delivered: item.deliveredQuantity
            })
        })

        const notDeliveredCount: NotDeliveredItem[] = newItems
            .filter(item => item.notDeliveredQuantity > 0)
            .map(item => ({
                itemId: item.itemId,
                quantity: item.notDeliveredQuantity
            }))
        const newCount = addNotDeliveredCount(deliveryOrder, notDeliveredCount)
        await updateDeliveryStats(delivery.orderId!, diffMap, {
            notDeliveredCount: newCount
        })

        const { loadSaleOperation } = saleOperationLoaders()
        const loadedOperation = await loadSaleOperation(delivery.table, delivery.operationId)
        const deliveredItems = newItems.filter(item => item.deliveredQuantity > 0)
        for (const item of deliveredItems) {
            const operationItem = loadedOperation!.items.find(operationItem => operationItem.id === item.itemId)!
            await decreaseReservedStock(operationItem.productId, deliveryOrder.branchId, item.deliveredQuantity, operationItem.lotId)
        }

        await refreshOperationDeliveryStatus(delivery.table, delivery.operationId)

        return savedDelivery
    }

    // Distributes a per-item quantity across the delivery orders (most recent first), limited by the given stat of each order item
    const distributeOverOrders = (
        deliveryOrders: DeliveryOrder[],
        itemId: string,
        quantity: number,
        getAvailable: (deliveryStats: DeliveryItemDeliveryStats) => number
    ) => {
        const distribution: { deliveryOrder: DeliveryOrder, quantity: number }[] = []
        let remaining = quantity
        for (const deliveryOrder of deliveryOrders) {
            if (remaining <= 0) break
            const orderItem = deliveryOrder.items.find(item => item.itemId === itemId)
            const orderQuantity = Math.min(remaining, orderItem ? getAvailable(orderItem.deliveryStats) : 0)
            if (orderQuantity <= 0) continue
            distribution.push({ deliveryOrder, quantity: orderQuantity })
            remaining -= orderQuantity
        }
        return distribution
    }

    const setOrderDiff = (
        ordersDiffMaps: Map<string, Map<string, Partial<DeliveryStatsDiff>>>,
        deliveryOrderId: string,
        itemId: string,
        diff: Partial<DeliveryStatsDiff>
    ) => {
        const orderDiffMap = ordersDiffMaps.get(deliveryOrderId) || new Map<string, Partial<DeliveryStatsDiff>>()
        orderDiffMap.set(itemId, diff)
        ordersDiffMaps.set(deliveryOrderId, orderDiffMap)
    }

    // Defective items go back to the operation pending (to be emitted again) and keep their stock reserved for the replacement.
    // Non defective items are cancelled from the operation and go back to the available stock.
    // In a financial hoard every returned item is cancelled, reducing the used credit.
    const returnShipmentItems = async (
        table: TableName,
        operationId: string,
        itemsData: Data2[],
        noteType: NoteGenerationType
    ) => {
        const { loadSaleOperation } = saleOperationLoaders()
        const loadedOperation = (await loadSaleOperation(table, operationId, true))!
        assertNoInvoiceInFlight(table, loadedOperation)
        const { loadOperationDeliveryOrders } = deliveryOrderLoaders()
        const loadedDeliveryOrders = await loadOperationDeliveryOrders(operationId, false)
        const sortedDeliveryOrders = loadedDeliveryOrders.sort((a, b) => b.deliveryDate.localeCompare(a.deliveryDate))
        const isFinancialHoard = hoardHelpers.isFinancialHoard(table, loadedOperation)
        // Entregado neto antes de esta devolución: define cuánto de lo devuelto no estaba facturado.
        const deliveredByItem = hoardHelpers.getNetDeliveredByItem(loadedDeliveryOrders)

        const returnItems = itemsData
            .map(itemData => ({
                itemId: itemData.itemId! as string,
                defectiveQuantity: (itemData.defectiveQuantity || 0) as number,
                nonDefectiveQuantity: (itemData.nonDefectiveQuantity || 0) as number
            }))
            .filter(item => item.defectiveQuantity + item.nonDefectiveQuantity > 0)

        const ordersDiffMaps = new Map<string, Map<string, Partial<DeliveryStatsDiff>>>()
        const operationDiffMap = new Map<string, Partial<ItemDeliveryStats>>()
        const cancelledQuantities = new Map<string, number>()
        returnItems.forEach(({ itemId, defectiveQuantity, nonDefectiveQuantity }) => {
            const distribution = distributeOverOrders(sortedDeliveryOrders, itemId, defectiveQuantity + nonDefectiveQuantity, deliveryStats => deliveryStats.delivered)
            const returnedQuantity = distribution.reduce((sum, { quantity }) => sum + quantity, 0)
            distribution.forEach(({ deliveryOrder, quantity }) => {
                setOrderDiff(ordersDiffMaps, deliveryOrder.id, itemId, {
                    toBeDelivered: -quantity,
                    delivered: -quantity,
                    returned: quantity
                })
            })

            const returnedDefective = isFinancialHoard ? 0 : Math.min(defectiveQuantity, returnedQuantity)
            const cancelledQuantity = returnedQuantity - returnedDefective
            operationDiffMap.set(itemId, {
                toBeEmitted: -cancelledQuantity,
                pending: returnedDefective,
                emitted: -returnedQuantity
            })
            cancelledQuantities.set(itemId, cancelledQuantity)
        })

        const updatedItems = loadedOperation.items.map(operationItem => {
            const cancelledQuantity = cancelledQuantities.get(operationItem.id) || 0
            return cancelledQuantity > 0 ? buildItem({
                ...operationItem,
                cancelledQuantity: (operationItem.cancelledQuantity || 0) + cancelledQuantity
            }) : operationItem
        })
        await updateOperationDeliveryStats(table, operationId, operationDiffMap, { items: updatedItems })

        for (const [deliveryOrderId, diffMap] of Array.from(ordersDiffMaps.entries())) {
            await updateDeliveryStats(deliveryOrderId, diffMap)
        }

        for (const { itemId, defectiveQuantity, nonDefectiveQuantity } of returnItems) {
            const operationItem = loadedOperation.items.find(item => item.id === itemId)!
            const { productId, lotId } = operationItem
            await increaseStock(productId, loadedOperation.branchId, nonDefectiveQuantity, lotId)
            if (!isFinancialHoard) {
                await reserveStock(productId, loadedOperation.branchId, defectiveQuantity, lotId)
            }
        }

        const code = await generateCode(TableName.DELIVERIES2)

        const deliveryItems = returnItems.map(({ itemId, defectiveQuantity, nonDefectiveQuantity }) => {
            const returnQuantity = defectiveQuantity + nonDefectiveQuantity
            return buildDeliveryItem({
                itemId,
                quantity: returnQuantity,
                deliveredQuantity: returnQuantity,
                notDeliveredQuantity: 0,
                defectiveQuantity
            })
        })
        const branch = branchLoaders().findBranch(loadedOperation.branchId)!
        const delivery = buildDelivery({
            code,
            codeStr: describeDeliveryCode({ code } as Delivery),
            table,
            operationId,
            method: DeliveryMethod.RETURN,
            items: deliveryItems,
            customerId: loadedOperation.customerId,
            branchId: loadedOperation.branchId,
            addressId: branch.address.id,
            deliveryStatus: DeliveryStatus.DELIVERED
        })

        const savedDelivery = await saveDelivery(delivery)

        await refreshOperationDeliveryStatus(table, operationId)

        // Las cantidades canceladas son las que se devuelven (en acopio financiero, todas).
        const adjustmentNoteItemsData = Array.from(cancelledQuantities.entries())
            .map(([itemId, quantity]) => ({ itemId, quantity }))
            .filter(({ quantity }) => quantity > 0.000001)

        if (adjustmentNoteItemsData.length > 0) {
            if (table === TableName.SALE_ORDERS) {
                // Ventas conservan el comportamiento actual: una sola nota.
                await createCreditNote(table, operationId, adjustmentNoteItemsData, loadedOperation, noteType)
                await cancelOperationIfNothingSold(table, operationId, updatedItems)
            } else if (isFinancialHoard) {
                // Un acopio financiero no devuelve plata: solo se anula fiscalmente lo facturado y no
                // cambia su valor, así que no se ajusta el importe adeudado.
                await createNotes(
                    table,
                    operationId,
                    loadedOperation,
                    adjustmentNoteItemsData,
                    NoteGenerationType.CREDIT_NOTE,
                    {
                        applyAdjustment: false,
                        createUninvoicedNote: false,
                        splitMode: 'return',
                        deliveredByItem
                    }
                )
            } else {
                await createNotes(table, operationId, loadedOperation, adjustmentNoteItemsData, noteType, {
                    splitMode: 'return',
                    deliveredByItem
                })
                await cancelOperationIfNothingSold(table, operationId, updatedItems)
            }
        }

        return savedDelivery
    }

    // Cancels first the quantities not emitted yet and then the pending quantities of the delivery orders (most recent first).
    // The cancelled quantities release their reserved stock.
    // With a delivery order id only the pending items of that order are cancelled (used when annulling its pending items)
    const cancelDeliveryItems = async (
        table: TableName,
        operationId: string,
        itemsData: Data2[],
        noteType: NoteGenerationType,
        deliveryOrderId?: string
    ) => {
        const { loadSaleOperation } = saleOperationLoaders()
        const loadedOperation = (await loadSaleOperation(table, operationId, true))!
        assertNoInvoiceInFlight(table, loadedOperation)
        const { loadOperationDeliveryOrders } = deliveryOrderLoaders()
        const loadedDeliveryOrders = await loadOperationDeliveryOrders(operationId, false)
        const isFinancialHoard = hoardHelpers.isFinancialHoard(table, loadedOperation)
        const sortedDeliveryOrders = loadedDeliveryOrders
            .filter(deliveryOrder => !deliveryOrder.cancelled && (!deliveryOrderId || deliveryOrder.id === deliveryOrderId))
            .sort((a, b) => b.deliveryDate.localeCompare(a.deliveryDate))

        const cancelItems = itemsData
            .map(itemData => ({
                itemId: itemData.itemId! as string,
                cancelQuantity: itemData.cancelQuantity! as number,
            }))
            .filter(item => item.cancelQuantity > 0)

        const ordersDiffMaps = new Map<string, Map<string, Partial<DeliveryStatsDiff>>>()
        const operationDiffMap = new Map<string, Partial<ItemDeliveryStats>>()
        const cancelledQuantities = new Map<string, number>()
        cancelItems.forEach(({ itemId, cancelQuantity }) => {
            const operationItem = loadedOperation.items.find(operationItem => operationItem.id === itemId)!
            const fromOperation = deliveryOrderId ? 0 : Math.min(cancelQuantity, operationItem.deliveryStats.pending)

            const distribution = distributeOverOrders(sortedDeliveryOrders, itemId, cancelQuantity - fromOperation, deliveryStats => deliveryStats.pending)
            const fromOrders = distribution.reduce((sum, { quantity }) => sum + quantity, 0)
            distribution.forEach(({ deliveryOrder, quantity }) => {
                setOrderDiff(ordersDiffMaps, deliveryOrder.id, itemId, {
                    toBeDelivered: -quantity,
                    pending: -quantity,
                    cancelled: quantity
                })
            })

            operationDiffMap.set(itemId, {
                toBeEmitted: -(fromOperation + fromOrders),
                pending: -fromOperation,
                emitted: -fromOrders
            })
            cancelledQuantities.set(itemId, fromOperation + fromOrders)
        })

        const updatedItems = loadedOperation.items.map(operationItem => {
            const cancelledQuantity = cancelledQuantities.get(operationItem.id) || 0
            return cancelledQuantity > 0 ? buildItem({
                ...operationItem,
                cancelledQuantity: (operationItem.cancelledQuantity || 0) + cancelledQuantity
            }) : operationItem
        })
        await updateOperationDeliveryStats(table, operationId, operationDiffMap, { items: updatedItems })

        for (const [deliveryOrderId, diffMap] of Array.from(ordersDiffMaps.entries())) {
            const deliveryOrder = sortedDeliveryOrders.find(deliveryOrder => deliveryOrder.id === deliveryOrderId)!
            const cancelledPending = Array.from(diffMap.entries()).map(([itemId, diff]) => ({ itemId, quantity: diff.cancelled || 0 }))
            const notDeliveredCount = removeNotDeliveredCount(deliveryOrder, cancelledPending)
            const updatedDeliveryOrder = await updateDeliveryStats(deliveryOrderId, diffMap, { notDeliveredCount })
            if (hasNothingToDeliver(updatedDeliveryOrder)) {
                await updateDeliveryOrderProps(deliveryOrderId, { cancelled: true })
            }
        }

        for (const { itemId } of cancelItems) {
            const operationItem = loadedOperation.items.find(item => item.id === itemId)!
            await enableStock(operationItem.productId, loadedOperation.branchId, cancelledQuantities.get(itemId) || 0, operationItem.lotId)
        }

        await refreshOperationDeliveryStatus(table, operationId)

        const adjustmentNoteItemsData = cancelItems
            .map(item => ({
                itemId: item.itemId,
                quantity: cancelledQuantities.get(item.itemId) || 0
            }))
            .filter(({ quantity }) => quantity > 0.000001)

        if (adjustmentNoteItemsData.length > 0) {
            if (table === TableName.SALE_ORDERS) {
                // Ventas conservan el comportamiento actual: una sola nota.
                await createCreditNote(table, operationId, adjustmentNoteItemsData, loadedOperation, noteType)
                await cancelOperationIfNothingSold(table, operationId, updatedItems)
            } else if (isFinancialHoard) {
                // Un acopio financiero no devuelve plata: solo se anula fiscalmente lo facturado y no
                // cambia su valor, así que no se ajusta el importe adeudado.
                await createNotes(
                    table,
                    operationId,
                    loadedOperation,
                    adjustmentNoteItemsData,
                    NoteGenerationType.CREDIT_NOTE,
                    { applyAdjustment: false, createUninvoicedNote: false, splitMode: 'cancel' }
                )
            } else {
                await createNotes(table, operationId, loadedOperation, adjustmentNoteItemsData, noteType, { splitMode: 'cancel' })
                await cancelOperationIfNothingSold(table, operationId, updatedItems)
            }
        }
    }

    // When every item was cancelled or returned without defects nothing is left sold, so the whole operation is cancelled
    // (not for financial hoards, whose hoarded value stays available)
    const cancelOperationIfNothingSold = async (
        table: TableName,
        operationId: string,
        items: Item[]
    ) => {
        const isFullyCancelled = items.every(item => (item.cancelledQuantity || 0) >= item.quantity)
        if (isFullyCancelled) {
            await updateOperationProps(table, operationId, { cancelled: true })
        }
    }

    // The credit note amount is registered on the operation: it lowers what the customer owes
    // and, if the operation was already paid, the excess becomes credit in favor
    const createCreditNote = async (
        table: TableName,
        operationId: string,
        itemsData: Data2[],
        operation: SaleOperation,
        noteType: NoteGenerationType
    ) => {
        const adjustmentNoteData = {
            type: AdjustmentNoteType.CREDIT,
            table,
            operationId
        }
        const savedAdjustmentNote = await createAdjustmentNote(adjustmentNoteData, itemsData, operation, noteType)
        await applyAdjustmentNote(table, operationId, savedAdjustmentNote.amount)
        return savedAdjustmentNote
    }

    const uploadDeliveryNoteFile = async (
        deliveryId: string,
        deliveryNoteFile: File
    ) => {
        const { loadDelivery } = deliveryloaders()
        const loadedDelivery = (await loadDelivery(deliveryId))!
        const { loadDeliveryOrder } = deliveryOrderLoaders()
        const deliveryOrder = loadedDelivery.orderId ? await loadDeliveryOrder(loadedDelivery.orderId) : undefined

        let deliveryNoteUrl: O<string>
        if (deliveryNoteFile) {
            const ext = fileHelpers.getFileExtension(deliveryNoteFile)
            const fileSubPath = deliveryNoteSubPath2(loadedDelivery, deliveryOrder, ext)
            deliveryNoteUrl = await saveFile(fileSubPath, deliveryNoteFile)
        }

        const delivery = buildDelivery({
            ...loadedDelivery,
            deliveryNoteUrl
        })
        
        return saveDelivery(delivery)
    }

    return {
        createDelivery,
        annulDelivery,
        completeShipment,
        returnShipmentItems,
        cancelDeliveryItems,
        uploadDeliveryNoteFile
    }
}
